import { useCallback } from 'react';

import { CircleCheckIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { BillingState, ModalPlanData } from '../../model';

interface Props {
  plan: ModalPlanData;
  onPlanSelect: (planId: string) => void;
  isLoading: boolean;
  billingState: BillingState;
  isPromoted?: boolean;
}

export const PlanCard = ({ plan, onPlanSelect, isLoading, billingState, isPromoted }: Props) => {
  const { t } = useTranslation();

  const isPlanCancelled = billingState?.is_canceled && billingState.has_expired;
  const isActive = billingState?.plan_id === plan.plan_id;
  const shouldShowButton = !isActive || isPlanCancelled;

  const handleClick = useCallback(() => {
    onPlanSelect(plan.plan_id);
  }, [onPlanSelect, plan.plan_id]);

  const isDowngrade = billingState?.plan_id === 'growth' && plan.plan_id === 'pro';
  const buttonVariant = isDowngrade || !isPromoted ? 'secondary' : 'primary';

  return (
    <div className="flex min-h-px min-w-px flex-1 flex-col items-end gap-6 rounded-lg border border-border px-4 pt-4 pb-6">
      <div className="flex items-start gap-4 self-stretch">
        <div className="flex min-h-px min-w-px flex-1 flex-col gap-0.5 whitespace-nowrap">
          <span className="text-xl leading-none font-semibold tracking-tight text-foreground">
            {plan.title}
          </span>
          <span className="text-sm leading-5 text-muted-foreground">{plan.subtitle}</span>
        </div>
        {isActive && (
          <Badge type="secondary" flavour="informational" rounded={false} className="font-semibold">
            {t('billing.current_plan')}
          </Badge>
        )}
      </div>
      <div className="flex flex-1 flex-col items-start gap-3 self-stretch">
        {plan.body?.map((item) => (
          <div key={item.text} className="flex w-full items-start gap-1.5">
            {item.number ? (
              <span className="w-6 shrink-0 text-center text-xs leading-4 font-medium text-primary">
                {item.number}
              </span>
            ) : (
              <div className="flex w-6 shrink-0 items-center justify-center">
                <CircleCheckIcon className="size-4 fill-info-soft text-primary" />
              </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-xs leading-4 font-medium text-foreground">
                {item.text}
              </span>
              {item.secondaryText && (
                <span className="truncate text-[8px] leading-[1.2] text-muted-foreground">
                  {item.secondaryText}
                </span>
              )}
            </div>
          </div>
        ))}
        <span className="px-1 text-xs leading-4 font-medium text-muted-foreground">
          {t('billing.more')}
        </span>
      </div>
      {shouldShowButton && (
        <Button onClick={handleClick} isLoading={isLoading} variant={buttonVariant}>
          {isDowngrade ? t('billing.downgrade') : t('billing.upgrade')}
        </Button>
      )}
      {!shouldShowButton && billingState.has_expired && (
        <Button onClick={handleClick} isLoading={isLoading} variant={buttonVariant}>
          {t('billing.renew')}
        </Button>
      )}
    </div>
  );
};
